import { authStore } from "@bebit/auth";
import { DateRange, DatePickerPreset } from "@bebit/types";
import { useSessionStorage, useLocalStorage } from "@vueuse/core";
import { computed, ComputedRef } from "vue";
import { usePresetFromStorages } from "@host/composables/storages/usePresetFromStorages";
import { getDateRangeOfPeriodChoice } from "@host/utils/date-picker";

type UserId = string;

type PeriodLocalStorage = Map<UserId, DateRange>;
type PeriodSessionStorage = Map<UserId, DateRange | undefined>;

export const STORAGE_KEY = "JOURNEY_PERIOD";

// eslint-disable-next-line max-lines-per-function
export function usePeriodFromStorages() {
  const { presetFromLocal, presetFromSession, presetFromStorages } = usePresetFromStorages();
  const { periodFromLocal, savePeriodToLocal } = usePeriodFromLocal();
  const { periodFromSession, savePeriodToSession } = usePeriodFromSession();

  const savePeriodToStorages = (newPeriod: DateRange) => {
    savePeriodToLocal(newPeriod);
    savePeriodToSession(newPeriod);
  };

  const periodFromStorages: ComputedRef<DateRange | undefined> = computed(() => {
    const sessionPeriodFromPreset: DateRange | undefined = getPeriodForPreset(presetFromSession.value);
    const localPeriodFromPreset: DateRange | undefined = getPeriodForPreset(presetFromLocal.value);

    if (sessionPeriodFromPreset) return sessionPeriodFromPreset;
    if (periodFromSession.value) return periodFromSession.value;

    if (localPeriodFromPreset) return localPeriodFromPreset;
    return periodFromLocal.value;
  });

  const isUsingDefaultPeriod: ComputedRef<boolean> = computed(() => {
    return !presetFromStorages.value && !periodFromStorages.value;
  });

  return { periodFromStorages, savePeriodToStorages, isUsingDefaultPeriod };
}

function usePeriodFromLocal() {
  const local = useLocalStorage<PeriodLocalStorage>(STORAGE_KEY, new Map<UserId, DateRange>());

  const savePeriodToLocal = (newPeriod: DateRange) => {
    local.value.set(getCurrentUserId(), newPeriod);
  };

  const periodFromLocal: ComputedRef<DateRange | undefined> = computed(() => {
    const periodStoredInLocal = local.value.get(getCurrentUserId());
    if (periodStoredInLocal)
      return {
        start: new Date(periodStoredInLocal.start),
        end: new Date(periodStoredInLocal.end),
      };
    return undefined;
  });

  return { periodFromLocal, savePeriodToLocal };
}

function usePeriodFromSession() {
  const session = useSessionStorage<PeriodSessionStorage>(STORAGE_KEY, new Map<UserId, DateRange | undefined>());

  const savePeriodToSession = (newPeriod: DateRange) => {
    session.value.set(getCurrentUserId(), newPeriod);
  };

  const periodFromSession: ComputedRef<DateRange | undefined> = computed(() => {
    const periodStoredInSession = session.value.get(getCurrentUserId());
    if (periodStoredInSession)
      return {
        start: new Date(periodStoredInSession.start),
        end: new Date(periodStoredInSession.end),
      };
    return undefined;
  });

  return { periodFromSession, savePeriodToSession };
}

function getCurrentUserId(): string {
  return authStore.user ? authStore.user?.id.toString() : "";
}

function getPeriodForPreset(preset: DatePickerPreset | undefined): DateRange | undefined {
  if (preset) return getDateRangeOfPeriodChoice(preset);
  return undefined;
}
